import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, ShieldCheck, Zap, Gem, Infinity as InfinityIcon } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

const TIERS = [
  {
    id: "free",
    name: "Free",
    icon: Zap,
    accent: "#64748B",
    monthly: 0,
    yearly: 0,
    original_monthly: 0,
    original_yearly: 0,
    features: [
      "10 AI analyses per day",
      "Last 3 videos in history",
      "Compare up to 2 videos",
      "13-dimension AI breakdown",
    ],
    cta: "Current",
  },
  {
    id: "pro",
    name: "Pro",
    icon: Sparkles,
    accent: "hsl(var(--primary))",
    monthly: 1000,
    yearly: 10000,
    original_monthly: 2000,
    original_yearly: 20000,
    features: [
      "50 AI analyses per day",
      "Last 3 videos in history",
      "Compare up to 2 videos",
      "No ad banners · priority",
    ],
    popular: false,
  },
  {
    id: "plus",
    name: "Plus",
    icon: Gem,
    accent: "#F59E0B",
    monthly: 2000,
    yearly: 20000,
    original_monthly: 4000,
    original_yearly: 40000,
    features: [
      "100 AI analyses per day",
      "Last 20 videos in history",
      "Compare up to 3 videos",
      "Priority support + autopay",
    ],
    popular: true,
  },
];

function fmtINR(amount) {
  if (!amount) return "₹0";
  return "₹" + amount.toLocaleString("en-IN");
}

export function PremiumUpgradeModal() {
  const { premiumOpen, closePremium } = useUI();
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState("monthly"); // monthly | yearly
  const [working, setWorking] = useState(null);  // tier id currently processing

  const isYearly = cycle === "yearly";

  useEffect(() => {
    if (!premiumOpen) {
      setBusy(false);
      setWorking(null);
    }
  }, [premiumOpen]);

  const openCheckout = async (tier) => {
    if (tier === "free") return;
    setBusy(true);
    setWorking(tier);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load Razorpay. Check your network and try again.");
        setBusy(false); setWorking(null);
        return;
      }
      let sub;
      try {
        const { data } = await api.post("/payments/create-subscription", { tier, cycle });
        sub = data;
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout.");
        setBusy(false); setWorking(null);
        return;
      }

      const rzp = new window.Razorpay({
        key: sub.key_id,
        subscription_id: sub.subscription_id,
        name: "VideoLens",
        description: `${sub.label} · Autopay`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#E11D48" },
        modal: {
          ondismiss: () => { setBusy(false); setWorking(null); },
        },
        handler: async (response) => {
          try {
            await api.post("/payments/verify-subscription", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              tier,
              cycle,
            });
            await refreshUser?.();
            toast.success(`Welcome to VideoLens ${tier[0].toUpperCase() + tier.slice(1)}!`);
            closePremium();
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed.");
          } finally {
            setBusy(false);
            setWorking(null);
          }
        },
      });

      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setBusy(false); setWorking(null);
      });
      rzp.open();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setBusy(false); setWorking(null);
    }
  };

  const currentTier = user?.plan || "free";

  return (
    <Dialog open={premiumOpen} onOpenChange={(o) => !o && !busy && closePremium()}>
      <DialogContent className="sm:max-w-3xl overflow-hidden" data-testid="premium-modal">
        <div className="absolute inset-0 grid-bg-sm radial-fade opacity-25 pointer-events-none" />
        <DialogHeader className="relative">
          <DialogTitle className="font-display text-2xl tracking-tight">
            Choose your <span className="gradient-text">VideoLens</span> plan
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Autopay via Razorpay · cancel anytime from Settings.
          </DialogDescription>
        </DialogHeader>

        {/* Promo strip */}
        <div className="relative mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center justify-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white tracking-wide">
            LAUNCH OFFER
          </span>
          <span className="text-emerald-700 dark:text-emerald-300 font-medium">
            Flat <span className="tabular">50% OFF</span> on every paid plan — original prices shown struck through
          </span>
        </div>

        {/* Billing toggle */}
        <div className="relative mt-2 mx-auto rounded-xl border border-border p-1 grid grid-cols-2 text-sm w-fit">
          <button
            onClick={() => setCycle("monthly")}
            data-testid="billing-monthly"
            disabled={busy}
            className={`rounded-lg px-6 py-2 font-medium transition-colors ${!isYearly ? "bg-secondary text-foreground" : "text-muted-foreground"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setCycle("yearly")}
            data-testid="billing-yearly"
            disabled={busy}
            className={`relative rounded-lg px-6 py-2 font-medium transition-colors ${isYearly ? "bg-secondary text-foreground" : "text-muted-foreground"}`}
          >
            Yearly
            <span className="absolute -top-2 -right-2 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              2 free
            </span>
          </button>
        </div>

        {/* Tier grid */}
        <div className="relative grid sm:grid-cols-3 gap-3 mt-4" data-testid="tier-grid">
          {TIERS.map((t) => {
            const price = isYearly ? t.yearly : t.monthly;
            const isCurrent = currentTier === t.id;
            const isBusy = working === t.id;
            const Icon = t.icon;
            return (
              <div
                key={t.id}
                data-testid={`tier-card-${t.id}`}
                className={`relative rounded-2xl border p-5 flex flex-col ${
                  t.popular ? "border-amber-500/60 bg-amber-500/5" : "border-border"
                } ${isCurrent ? "ring-2 ring-primary/50" : ""}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 right-4 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">
                    MOST POPULAR
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ background: `${t.accent}22`, color: t.accent }}
                  >
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <p className="font-display text-lg font-bold tracking-tight">{t.name}</p>
                </div>

                <div className="mt-4">
                  {t.id === "free" ? (
                    <p className="font-display text-3xl font-bold tabular">Free</p>
                  ) : (
                    <>
                      {/* Original price (crossed out) */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground line-through tabular">
                          {fmtINR(isYearly ? t.original_yearly : t.original_monthly)}
                        </span>
                        <span className="rounded-full bg-emerald-500/15 text-emerald-500 px-1.5 py-0.5 text-[9px] font-bold tracking-wide">
                          50% OFF
                        </span>
                      </div>
                      <p className="font-display font-bold mt-0.5">
                        <span className="text-3xl tabular">{fmtINR(price)}</span>
                        <span className="text-sm text-muted-foreground">/{isYearly ? "yr" : "mo"}</span>
                      </p>
                      {isYearly && (
                        <p className="text-[11px] text-emerald-500 tabular mt-0.5">
                          = {fmtINR(Math.round(price / 12))}/mo
                        </p>
                      )}
                    </>
                  )}
                </div>

                <ul className="mt-4 space-y-2 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5">
                  {t.id === "free" ? (
                    <Button variant="outline" disabled className="w-full h-10" data-testid={`tier-cta-${t.id}`}>
                      {isCurrent ? "Current plan" : "Free forever"}
                    </Button>
                  ) : isCurrent ? (
                    <Button variant="outline" disabled className="w-full h-10" data-testid={`tier-cta-${t.id}`}>
                      Active
                    </Button>
                  ) : (
                    <Button
                      onClick={() => openCheckout(t.id)}
                      disabled={busy}
                      className={`w-full h-10 gap-2 ${t.popular ? "bg-amber-500 hover:bg-amber-500/90 text-white" : ""}`}
                      data-testid={`tier-cta-${t.id}`}
                    >
                      {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <InfinityIcon className="h-4 w-4" />}
                      {isBusy ? "Opening…" : `Start ${t.name} · ${isYearly ? "yr" : "mo"}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="relative text-[11px] text-muted-foreground text-center mt-3 flex items-center justify-center gap-1">
          <ShieldCheck className="h-3 w-3" /> Secured by Razorpay · UPI AutoPay, cards &amp; netbanking · test mode
        </p>
      </DialogContent>
    </Dialog>
  );
}
